import { router } from '@inertiajs/react'
import type { ReactNode } from 'react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/layouts/AdminLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface SuggestedEntry {
  id: string
  code: string
  classification: string
  display_name: string | null
}

interface ReviewItem {
  id: string
  raw_value: string
  field_type: string
  source: string | null
  status: string
  occurrence_count: number
  resolution_method: string | null
  similarity_score: number | null
  llm_confidence: number | null
  llm_reasoning: string | null
  suggested_entry: SuggestedEntry | null
  created_at: string
}

interface Props {
  items: ReviewItem[]
  counts: { pending: number; suggested: number; resolved: number; dismissed: number }
  current_status: string
}

const tabs = [
  { key: 'pending', label: 'Pending' },
  { key: 'suggested', label: 'Suggested' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'dismissed', label: 'Dismissed' },
] as const

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function resolutionBadge(method: string | null): ReactNode {
  if (!method) return null
  const colors: Record<string, string> = {
    trgm_auto: 'bg-blue-100 text-blue-800',
    llm_auto: 'bg-green-100 text-green-800',
    llm_suggested: 'bg-yellow-100 text-yellow-800',
    manual: 'bg-purple-100 text-purple-800',
  }
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', colors[method] || 'bg-gray-100 text-gray-800')}>
      {method.replace('_', ' ')}
    </span>
  )
}

export default function TaxonomyReviewsIndex({ items, counts, current_status }: Props) {
  function switchTab(status: string) {
    router.get('/admin/taxonomy_reviews', { status }, { preserveState: true })
  }

  function accept(id: string) {
    router.post(`/admin/taxonomy_reviews/${id}/accept`)
  }

  function dismiss(id: string) {
    router.post(`/admin/taxonomy_reviews/${id}/dismiss`, { return_status: current_status })
  }

  const showActions = current_status === 'suggested' || current_status === 'pending'

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Taxonomy Reviews</h1>

      {/* Status tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => switchTab(tab.key)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              current_status === tab.key
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
            <Badge
              variant={current_status === tab.key ? 'default' : 'secondary'}
              className="text-xs px-1.5 py-0"
            >
              {counts[tab.key as keyof typeof counts]}
            </Badge>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No {current_status} review items.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Raw Value</TableHead>
              <TableHead>Field Type</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Occurrences</TableHead>
              <TableHead>Resolution</TableHead>
              <TableHead>Suggested Match</TableHead>
              <TableHead>Age</TableHead>
              {showActions && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium font-mono text-sm">{item.raw_value}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">{item.field_type}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{item.source || '—'}</TableCell>
                <TableCell className="text-right font-mono">{item.occurrence_count}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {resolutionBadge(item.resolution_method)}
                    {item.similarity_score != null && (
                      <span className="text-xs text-muted-foreground">trgm: {item.similarity_score.toFixed(2)}</span>
                    )}
                    {item.llm_confidence != null && (
                      <span className="text-xs text-muted-foreground">llm: {item.llm_confidence.toFixed(2)}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {item.suggested_entry ? (
                    <div>
                      <div className="text-sm font-medium">{item.suggested_entry.classification}</div>
                      <div className="text-xs text-muted-foreground">{item.suggested_entry.code}</div>
                      {item.llm_reasoning && (
                        <div className="text-xs text-muted-foreground mt-1 max-w-xs truncate" title={item.llm_reasoning}>
                          {item.llm_reasoning}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                  {timeAgo(item.created_at)}
                </TableCell>
                {showActions && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {item.suggested_entry && (
                        <Button size="sm" variant="default" onClick={() => accept(item.id)}>
                          Accept
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => dismiss(item.id)}>
                        Dismiss
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

TaxonomyReviewsIndex.layout = (page: ReactNode) => (
  <AppLayout>
    <AdminLayout>{page}</AdminLayout>
  </AppLayout>
)
